/* eslint no-mixed-operators: 0 */

import { ajax, async_ajax } from 'ajax';
import { notify } from 'notify';
import { useMainStore } from 'store';
import { computed, nextTick, ref, toRaw } from 'vue';

const timezones = ref( [] );

const timezones_fetched = ref( false );

const timezones_loaded = ref( false );

const job_functions = ref( [] );

const job_functions_fetched = ref( false );

const job_functions_loaded = ref( false );

export function get_label( slug, value ) {
	const store = useMainStore();

	for( const item of store.lists[slug] ) {
		if( item.value === value ) {
			return item.label;
		}
	}

	return value;
}

// The ListItem Description beside the label, for a value whose label cannot
// explain itself. Same shape as get_label above and the same fallback rule: a
// value the list does not carry gets nothing rather than an invented string.
export function get_description( slug, value ) {
	const store = useMainStore();

	for( const item of store.lists[slug] ) {
		if( item.value === value ) {
			return item.description ?? ``;
		}
	}

	return ``;
}

export function has_permission( permissions ) {
	const store = useMainStore();

	if( !store.user ) {
		return false;
	}

	if( store.user.is_superadmin ) {
		return true;
	}

	return permissions.some( permission => store.user.permissions.includes( permission ) );
}

export function get_subscriptions_with_feature( feature ) {
	const store = useMainStore();

	return store.lists.organization_subscriptions.filter( item => item.features.includes( feature ) );
}

export async function maybe_load_temporal() {
	if( window.Temporal ) {
		return;
	}

	const { Temporal } = await import( `@js-temporal/polyfill` );

	// eslint-disable-next-line require-atomic-updates
	window.Temporal = Temporal;
}

function parse_date( s ) {
	const datetime = s.replace( ` `, `T` );

	if( !datetime.includes( `T` ) ) {
		return Temporal.PlainDate.from( datetime );
	}

	try {
		return Temporal.Instant.from( datetime );
	} catch {
		return Temporal.PlainDateTime.from( datetime );
	}
}

export function get_date( datetime ) {
	if( !datetime || datetime.indexOf( `0001-01-01` ) === 0 ) {
		return ``;
	}

	const dt = parse_date( datetime );

	return dt.toLocaleString(
		undefined, // eslint-disable-line no-undefined
		{
			month: `2-digit`,
			day: `2-digit`,
			year: `numeric`
		}
	);
}

export function get_current_date() {
	const dt = Temporal.Now.plainDateISO();

	return dt.toLocaleString(
		undefined, // eslint-disable-line no-undefined
		{
			month: `2-digit`,
			day: `2-digit`,
			year: `numeric`
		}
	);
}

export function get_datetime( datetime ) {
	if( datetime.indexOf( `0001-01-01` ) === 0 ) {
		return ``;
	}

	const dt = parse_date( datetime );

	return dt.toLocaleString(
		undefined, // eslint-disable-line no-undefined
		{
			month: `2-digit`,
			day: `2-digit`,
			year: `numeric`,
			hour: `2-digit`,
			minute: `2-digit`
		}
	);
}

export function get_weekday_label( index ) {
	const today = Temporal.Now.plainDateISO();

	const offset = index - ( today.dayOfWeek % 7 );

	const dt = today.add( {
		days: offset
	} );

	return dt.toLocaleString(
		undefined, // eslint-disable-line no-undefined
		{
			weekday: `long`
		}
	);
}

export function timezones_are_loaded() {
	return timezones_loaded.value;
}

export function get_timezone_groups() {
	if( !timezones_fetched.value ) {
		timezones_fetched.value = true;

		ajax( {
			method: `GET`,
			endpoint: `timezones`,
			on_success: response => {
				timezones.value = response ?? [];
				timezones_loaded.value = true;
			}
		} );
	}

	const popular = {
		'America/Los_Angeles': `Pacific`,
		'America/New_York': `Eastern`,
		'America/Chicago': `Central`,
		'America/Denver': `Mountain`,
		'Asia/Kolkata': `India`
	};

	const groupsByLabel = {
		Popular: []
	};

	for( const item of timezones.value ) {
		if( Object.hasOwn( popular, item.name ) ) {
			groupsByLabel.Popular.push(
				{
					value: item.name,
					label: popular[item.name]
				}
			);
		} else {
			const parts = item.name.split( `/` );

			const group = parts.shift();
			if( !Object.hasOwn( groupsByLabel, group ) ) {
				groupsByLabel[group] = [];
			}

			// .replace(/_/g, ' ')
			groupsByLabel[group].push(
				{
					value: item.name,
					label: parts.join( `/` )
				}
			);
		}
	}

	return groupsByLabel;
}

export function job_functions_are_loaded() {
	return job_functions_loaded.value;
}

export function get_job_function_groups( seniority ) {
	const store = useMainStore();

	if( !job_functions_fetched.value ) {
		job_functions_fetched.value = true;

		ajax( {
			method: `GET`,
			endpoint: store.site.portal === `admin` ? `api/members/job_functions` : `job_functions`,
			on_success: response => {
				job_functions.value = response ?? [];
				job_functions_loaded.value = true;
			}
		} );
	}

	const groupsByLabel = {};

	for( const item of job_functions.value ) {
		if( seniority && !item.seniorities.includes( seniority ) ) {
			continue;
		}

		if( !Object.hasOwn( groupsByLabel, item.function ) ) {
			groupsByLabel[item.function] = [];
		}

		groupsByLabel[item.function].push(
			{
				value: item.uuid,
				// `sub_function` is always present as a string (Go never
				// omits it), so it is `` for a row with no sub function --
				// never null/undefined. `??` only falls back on
				// null/undefined, so every C-level and generic-IT row (the
				// whole vocabulary with no sub function) rendered a
				// blank-labeled option instead of falling back to the
				// function name. Same divergence JobFunctionName in
				// cms/audience.go was fixed for (#1208); this call site was
				// missed.
				label: item.sub_function === `` ? item.function : item.sub_function
			}
		);
	}

	return groupsByLabel;
}

export function on_seniority_change( item ) {
	if( item.job_function_uuid === `` ) {
		return;
	}

	if( item.seniority === `` ) {
		item.job_function_uuid = ``;
		return;
	}

	const groups = get_job_function_groups( item.seniority );
	for( const key of Object.keys( groups ) ) {
		for( const job_function of groups[key] ) {
			if( job_function.value === item.job_function_uuid ) {
				return;
			}
		}
	}

	item.job_function_uuid = ``;
}

export function copy( obj ) {
	return structuredClone( toRaw( obj ) );
}

export function get_comma_separated_list( items ) {
	switch( items.length ) {
	case 0:
		return ``;
	case 1:
		return items[0];
	case 2:
		return `${items[0]} and ${items[1]}`;
	}

	const last = items.at( -1 );
	const rest = items.slice( 0, -1 );

	return `${rest.join( `, ` )}, and ${last}`;
}

export function scroll_to_first_error( errors ) {
	nextTick(
		() => {
			if( !Array.isArray( errors ) ) {
				return;
			}

			for( const error of errors ) {
				const el = document.getElementById( `${error.field}-error` );
				if( !el ) {
					notify( `error`, error.message );
					continue;
				}

				const container_el = el.closest( `.field, .table, section` );
				if( !container_el ) {
					notify( `error`, error.message );
					continue;
				}

				container_el.scrollIntoView( true );

				return;
			}
		}
	);
}

export function computed_array( obj, field ) {
	return computed(
		{
			get() {
				return obj[field] ? obj[field].join( `\n` ) : ``;
			},
			set( value ) {
				obj[field] = value.split( `\n` ).filter( item => item !== `` );
			}
		}
	);
}

export function buffer_decode( value ) {
	// Convert base64url to base64
	const base64 = value.replace( /-/g, `+` ).replace( /_/g, `/` );
	// Add padding if needed
	const padded = base64 + `=`.repeat( ( 4 - base64.length % 4 ) % 4 );
	// Decode base64 to binary string
	const binaryString = atob( padded );
	// Convert to Uint8Array
	return Uint8Array.from( binaryString, c => c.charCodeAt( 0 ) );
}

export function buffer_encode( value ) {
	// Convert ArrayBuffer/Uint8Array to binary string
	const binaryString = String.fromCharCode( ...new Uint8Array( value ) );
	// Encode to base64
	const base64 = btoa( binaryString );
	// Convert to base64url (URL-safe base64)
	return base64.replace( /\+/g, `-` ).replace( /\//g, `_` )
		.replace( /[=]/g, `` );
}

export function handle_error( errors, error ) {
	if( typeof error === `string` ) {
		notify( `error`, error );
	} else if( error.message ) {
		notify( `error`, error.message );
	} else {
		errors.value = error;
		scroll_to_first_error( error );
	}
}

export function get_cdn_url( image_url, width, is_public ) {
	const store = useMainStore();

	const url = new URL( image_url );

	if( store.site.environment === `production` && is_public ) {
		url.pathname = `/cdn-cgi/image/w=${width}/${image_url}`;
	} else {
		url.searchParams.set( `w`, width );
	}

	return url.toString();
}

function is_redirect_host_allowed( hostname ) {
	const h = hostname.toLowerCase();

	const store = useMainStore();

	if( !store.allowed_redirect_domains || store.allowed_redirect_domains.length === 0 ) {
		return h === location.hostname.toLowerCase();
	}

	return store.allowed_redirect_domains.includes( h );
}

// is_app_handoff_host_allowed answers a DIFFERENT question from
// is_redirect_host_allowed above, against a different list.
//
// safe_redirect's list is the sites rows: the hosts mono itself serves. The
// application a completed signup hands off to is not one of those and must not
// become one (see core/app_handoff.go), so widening that list to reach it would
// also widen the guard on a magic link's redirect_to, which has no business
// going there.
//
// Compared on `host` rather than `hostname` because these entries may carry a
// port: a local development override is "localhost:3000", and matching it on the
// hostname alone would admit any port on the machine.
function is_app_handoff_host_allowed( host ) {
	const h = host.toLowerCase();

	const store = useMainStore();

	if( !store.app_handoff_domains || store.app_handoff_domains.length === 0 ) {
		return false;
	}

	return store.app_handoff_domains.includes( h );
}

// safe_app_handoff navigates to a destination the SERVER already resolved and
// validated (core.ResolveAppHandoffURL), and refuses anything else.
//
// The server is where the decision is made; this is the second lock. It exists
// because the destination crosses to another deployment, and the person arriving
// there has just proved control of their mailbox, which is the worst possible
// moment to be dropped on a hostile page.
//
// Returns false rather than navigating when it will not go, so the caller can
// leave the visitor somewhere real instead of on a page that silently did
// nothing.
export function safe_app_handoff( s ) {
	if( !s ) {
		return false;
	}

	try {
		const target = new URL( s );

		if( target.protocol !== `https:` && target.protocol !== `http:` ) {
			return false;
		}

		if( !is_app_handoff_host_allowed( target.host ) ) {
			return false;
		}

		location.href = target.href;

		return true;
	} catch {
		return false;
	}
}

// land_after_login is where a member goes the instant they finish authenticating
// in the members portal -- password, magic link, passkey or OTP, all four.
//
// # The defect, reported by Matt 2026-09-16
//
// It used to be safe_redirect( redirect_to ) alone, and safe_redirect with
// nothing to redirect TO reloads the page, which lands on this portal's own
// profile screen. That is right for somebody who came here to change their
// email preferences and wrong for everybody else: a member who signed in with
// no pending destination is, overwhelmingly, a customer trying to reach their
// product, and the portal was making them find "Go to app", then press
// "Continue with Wiretap" on the application's own sign-in page, before getting
// there. Two clicks and two screens after they had already proved who they are.
//
// # The order, and why the fallback is still a reload
//
// A pending redirect_to always wins: it is a destination somebody explicitly
// asked for -- an unfinished OAuth authorization (oauth2/members_api.go), or a
// link the member opened -- and overriding it with the app would throw that
// away and strand them.
//
// With nothing pending, store.app_sign_in_url is this BRAND's application with
// the flag that skips its sign-in button (core.BrandAppSignInURL, resolved
// server-side -- the host is never assembled here). It is empty for a brand
// that has no application, and safe_app_handoff independently refuses a host
// that is not on the handoff allowlist, so both of those fall through to the
// reload, which is exactly the behaviour every member has today. Nobody who
// currently lands on their profile can be sent anywhere new by this except a
// member of a brand that actually has an app to be sent to.
// The OpenID Connect login_hint an app sent with its authorize request
// (mono#1837, insights#5919's email-first sign-in). oauth2/members_api.go
// never hands it to the portal on its own: it rides INSIDE the pending
// /oauth2/authorize URL the login page receives as redirect_to, so this is
// where a form that wants to pre-fill an address has to look for it.
//
// Same-origin only. redirect_to is whatever is in the address bar, and while
// safe_redirect() vets it before anyone navigates there, pre-filling a form
// from a foreign URL's query would still let a crafted link put words in the
// email box. The server already normalized and length-capped the value
// (oauth2/login_hint.go); the shape check here is belt and braces for a URL
// nobody signed.
export function login_hint_from_redirect( redirect_to ) {
	if( !redirect_to || typeof redirect_to !== `string` ) {
		return ``;
	}

	let url = null;

	try {
		url = new URL( redirect_to, location.origin );
	} catch {
		return ``;
	}

	if( url.origin !== location.origin || url.pathname !== `/oauth2/authorize` ) {
		return ``;
	}

	const hint = ( url.searchParams.get( `login_hint` ) || `` ).trim();

	if( hint.length === 0 || hint.length > 254 || !/^[^\s@<>"'(),;:\\[\]]+@[^\s@<>"'(),;:\\[\]]+$/.test( hint ) ) {
		return ``;
	}

	return hint;
}

// # The passkey upgrade prompt (#2359)
//
// { offer_passkey: true } is passed by the sign-ins that did NOT use a passkey
// -- password, emailed link, OTP -- and only by them. Before landing, it asks
// the server whether to offer "Add a passkey that works in Wiretap, Decision
// Insights and Stringer"; the server says yes only on a wiretaplabs.com host,
// to a member with no passkey there, at most three times. A yes goes to the
// passkey-upgrade page, which lands the member exactly here once they add one
// or skip. Any failure of the question lands them as if it had not been asked:
// a prompt must never be why a sign-in does not finish.
export async function land_after_login( redirect_to, options = {} ) {
	if( options.offer_passkey && `PublicKeyCredential` in window ) {
		try {
			const response = await async_ajax( {
				method: `POST`,
				endpoint: `api/members/passkey/upgrade_prompt`
			} );

			if( response && response.show ) {
				const params = new URLSearchParams();

				if( redirect_to ) {
					params.set( `redirect_to`, redirect_to );
				}

				if( response.has_legacy_passkey ) {
					params.set( `legacy`, `1` );
				}

				const query = params.toString();

				location.href = `/passkey-upgrade${query ? `?${query}` : ``}`;

				return;
			}
		} catch {
			// Fall through and land.
		}
	}

	if( redirect_to ) {
		safe_redirect( redirect_to );

		return;
	}

	const store = useMainStore();

	if( safe_app_handoff( store.app_sign_in_url ) ) {
		return;
	}

	safe_redirect( `` );
}

export function safe_redirect( s ) {
	let url = s;

	if( Array.isArray( url ) ) {
		url = url[0];
	}

	if( !url ) {
		location.reload();
		return;
	}

	try {
		const target = new URL( url, location.origin );

		if( target.protocol !== `https:` ) {
			location.reload();
			return;
		}

		if( !is_redirect_host_allowed( target.hostname ) ) {
			location.reload();
			return;
		}

		location.href = target.href;
	} catch {
		location.reload();
	}
}

